<script>
/**
 * Mixin for filter and sorting options in dmp/pending.vue and archives.vue
 * Most computed return default values for the main `pendOptions` object.
 * Same behaviour as ./problems.vue.
 */

// Vuex
import { mapState } from 'vuex';
import { FETCH_USERS } from '@/store/action-types';
import { GET_USERS } from '@/store/mutation-types';
import { convertImpactFilterToArray } from '@/utils/problem-utils';

export default {
  computed: {
    pendOptions() {
      return [
        {
          key: 'sortTitle',
          component: 'h2',
          options: { class: 'mt5em mb5em basic-sub-title' },
          slots: [{
            component: 'span',
            content: this.$t('message.WIDGET_PB_SORT'),
          }],
        },
        {
          key: 'sortBy',
          component: 'RadioGroup',
          value: this.pendOptSortBy,
          options: {
            items: this.pendOptSortItems,
            sortable: true,
            class: 'mb10',
          },
        },
        {
          key: 'filtersTitle',
          component: 'h2',
          options: { class: 'mt5em mb5em basic-sub-title' },
          slots: [{
            component: 'span',
            content: this.$t('message.FILTERS'),
          }],
        },
        {
          key: 'leaderTitle',
          component: 'div',
          options: { class: 'legend mt5em mb5em' },
          slots: [{
            component: 'span',
            content: this.$t('message.LEADER'),
          }],
        },
        {
          key: 'leader',
          component: 'CheckboxList',
          value: this.pendOptLeader,
          options: {
            items: this.pendOptUserItems,
          },
        },
        {
          key: 'impactRangeTitle',
          component: 'div',
          options: {
            class: 'legend d-inline-block mt10 mb10',
          },
          slots: [{
            component: 'span',
            content: this.$t('message.IMPACT'),
          }],
        },
        {
          key: 'impactRange',
          value: this.pendOptImpactRange,
          component: 'ImpactSelector',
          options: {
            multiple: true,
            min: true,
          },
        },
        /**
         *
         * WARNING: These options will only be displayed in the nav menu settings
         * as their purpose is to add content to the nav items
         *
         */
        {
          key: 'displayNumberOfPendingActionsTitle',
          component: 'h2',
          options: { class: 'mt5em mb5em basic-sub-title' },
          slots: [{
            component: 'span',
            content: this.$t('message.DISPLAY'),
          }],
        },
        {
          key: 'displayNumberOfPendingActions',
          component: 'Checkbox',
          value: this.pendOptNumberOfPendingActions,
          options: {
            title: this.$t('message.DISPLAY_NUMBER_OF_ACTIONS_PENDING'),
          },
        },
      ];
    },
    pendOptSortBy() {
      if (this.allPageOptions && this.allPageOptions.sortBy) {
        return this.allPageOptions.sortBy;
      }
      return { value: 3, desc: true };
    },
    /**
     * Available sorting options for pending/archived actions
     * NOTE: This is overriden in dmp/archives
     * @return {Array}
     */
    pendOptSortItems() {
      return [
        { id: 0, label: this.$t('message.SORT_BY_TITLE') },
        { id: 1, label: this.$t('message.SORT_BY_ENTRY_DATE') },
        { id: 2, label: this.$t('message.SORT_BY_DEADLINE') },
        { id: 3, label: this.$t('message.SORT_BY_IMPACT') },
      ];
    },
    pendOptLeader() {
      if (this.allPageOptions && this.allPageOptions.leader) {
        return this.allPageOptions.leader;
      }
      return [];
    },
    pendOptNumberOfPendingActions() {
      if (this.allPageOptions && this.allPageOptions.displayNumberOfPendingActions) {
        return this.allPageOptions.displayNumberOfPendingActions;
      }
      return false;
    },
    pendOptUserItems() {
      const usersData = this.pendOptUsersData || [];
      return [
        { id: 0, label: this.$t('message.UNASSIGNED_FEM') },
        ...usersData.map(user => ({ id: Number(user.id), label: this.$t('fullNameFormat', user) })),
      ];
    },
    pendOptImpactRange() {
      const allImpactValues = [0, 1, 2, 3];
      if (this.impactRange && this.allPageOptions.impactRange) {
        return convertImpactFilterToArray(this.allPageOptions.impactRange);
      }
      return allImpactValues;
    },
    /** If defined, contains all the options above */
    allPageOptions() {
      return null;
    },
    ...mapState({
      pendOptUsersData: state => state[GET_USERS.stateKey],
      pendOptUsersLoading: state => state[GET_USERS.loadingKey],
    }),
  },
  created() {
    // Fetch all users
    if (!this.pendOptUsersLoading && this.App.user && this.App.user.id) {
      this.$store.dispatch(FETCH_USERS);
    }
  },
  methods: {
    /** Create a string formatted as query params with pendOptions params */
    pendOptQueryParams(options) {
      if (!options) {
        return '';
      }
      let query = '';
      let amp = '';
      if (options.sortBy) {
        query += `sortBy=${options.sortBy.value}&sortDesc=${options.sortBy.desc}`;
        amp = '&';
      }
      if (options.impactRange) {
        const impactRange = convertImpactFilterToArray(options.impactRange);
        query += `${amp}impactFilter=${impactRange.join(',')}`;
        amp = '&';
      }
      if (options.leader) {
        query += `${amp}leader=${options.leader.join(',')}`;
      }
      return query;
    },
    pendOptUserLoaded() {
      this.$store.dispatch(FETCH_USERS);
    },
  },
};
</script>
